import { useMemo, useState } from "react";
import { Button, Form, Input, Modal, Pagination, Select, Table, TablePaginationConfig, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "react-query";
import Layout from "../../Containers/Layout";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import {
  AssociatedResultTemplateDTO,
  CompetenceTemplateDTO,
  CompetenceTemplateUpdateDTO,
  LearningCompetenceTypeEnum
} from "../../Api";
import {
  addCompetenceTemplate,
  deleteCompetenceTemplate,
  getCompetenceTemplates,
  updateCompetenceTemplate
} from "../../Requests/competence-template-requests";
import ProgramCompetenceForm from "../StudyPrograms/StudyProgramForm/ProgramCompetenceForm";
import styles from "../Domains/Domains.module.scss";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

const { Search } = Input;
const { Option } = Select;

const competenceTypeLabel = (type?: LearningCompetenceTypeEnum) => {
  switch (type) {
    case LearningCompetenceTypeEnum.KeyCompetence:
      return "Competență cheie";
    case LearningCompetenceTypeEnum.GreenCompetence:
      return "Competență verde";
    case LearningCompetenceTypeEnum.DigitalCompetence:
      return "Competență digitală";
    default:
      return "-";
  }
};

const getServerErrorMessage = (error: any) =>
  (typeof error?.body === "string" && error.body) ||
  (typeof error?.body?.message === "string" && error.body.message) ||
  (typeof error?.response?.data === "string" && error.response.data) ||
  (typeof error?.response?.data?.message === "string" && error.response.data.message);

const toFormResults = (results: AssociatedResultTemplateDTO[] | null | undefined, type: LearningCompetenceTypeEnum) => (
  (results || [])
    .filter((e) => e.type === type)
    .map((e) => ({
      id: e.id,
      description: e.description ?? "",
      type: e.type
    }))
);

const CompetenceTemplates = () => {
  const [form] = Form.useForm<{
    description: string;
    type: LearningCompetenceTypeEnum;
    knowledgeResults: any[];
    skillResults: any[];
    responsibilityResults: any[];
  }>();

  const queryClient = useQueryClient();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CompetenceTemplateDTO | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<LearningCompetenceTypeEnum | undefined>(undefined);

  const { data, isLoading } = useQuery(
    ["getCompetenceTemplates", searchTerm, typeFilter, currentPage, pageSize],
    () => getCompetenceTemplates(searchTerm, typeFilter, currentPage, pageSize),
    {
      onError: () => {
        openNotification(
          "Eroare!",
          "Datele nu au putut fi aduse din server!",
          NOTIFICATION_TYPES.ERROR
        );
      }
    }
  );

  const typeOptions = useMemo(
    () => [
      LearningCompetenceTypeEnum.KeyCompetence,
      LearningCompetenceTypeEnum.GreenCompetence,
      LearningCompetenceTypeEnum.DigitalCompetence
    ],
    []
  );

  const openCreateEditor = () => {
    setEditingTemplate(undefined);
    form.resetFields();
    form.setFieldsValue({
      description: "",
      type: LearningCompetenceTypeEnum.KeyCompetence,
      knowledgeResults: [],
      skillResults: [],
      responsibilityResults: []
    });
    setIsEditorOpen(true);
  };

  const openEditEditor = (record: CompetenceTemplateDTO) => {
    setEditingTemplate(record);
    form.resetFields();
    form.setFieldsValue({
      description: record.description ?? "",
      type: record.type,
      knowledgeResults: toFormResults(record.associatedResultTemplates, LearningCompetenceTypeEnum.Knowledge),
      skillResults: toFormResults(record.associatedResultTemplates, LearningCompetenceTypeEnum.Skill),
      responsibilityResults: toFormResults(record.associatedResultTemplates, LearningCompetenceTypeEnum.ResponsibilityAutonomy),
    });
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingTemplate(undefined);
    form.resetFields();
  };

  const openSaveError = (error: any) => {
    const serverMessage = getServerErrorMessage(error);
    openNotification(
      "Eroare!",
      serverMessage || "Datele completate nu sunt valide!",
      NOTIFICATION_TYPES.ERROR
    );
  };

  const saveTemplate = async () => {
    try {
      const values = await form.validateFields();
      const associatedResults = [
        ...(values.knowledgeResults || []).map((e: any) => ({
          id: e.id,
          description: (e.description || "").trim(),
          type: LearningCompetenceTypeEnum.Knowledge
        })),
        ...(values.skillResults || []).map((e: any) => ({
          id: e.id,
          description: (e.description || "").trim(),
          type: LearningCompetenceTypeEnum.Skill
        })),
        ...(values.responsibilityResults || []).map((e: any) => ({
          id: e.id,
          description: (e.description || "").trim(),
          type: LearningCompetenceTypeEnum.ResponsibilityAutonomy
        })),
      ];

      const payload: CompetenceTemplateUpdateDTO = {
        description: values.description?.trim(),
        type: values.type,
        associatedResults
      };

      if (editingTemplate?.id) {
        await updateCompetenceTemplate(editingTemplate.id, payload);
      } else {
        await addCompetenceTemplate(payload);
      }

      openNotification(
        "Succes!",
        "Salvarea s-a efectuat cu succes!",
        NOTIFICATION_TYPES.SUCCESS
      );
      closeEditor();
      queryClient.invalidateQueries("getCompetenceTemplates");
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openSaveError(error);
    }
  };

  const confirmDelete = (id?: string) => {
    if (!id) {
      return;
    }

    Modal.confirm({
      title: "Ștergere competență",
      content: "Sigur doriți ștergerea competenței?",
      okText: "Confirmă",
      cancelText: "Anulare",
      onOk: async () => {
        try {
          await deleteCompetenceTemplate(id);
          openNotification(
            "Succes!",
            "Ștergerea s-a realizat cu succes!",
            NOTIFICATION_TYPES.SUCCESS
          );
          queryClient.invalidateQueries("getCompetenceTemplates");
        } catch (error: any) {
          openSaveError(error);
        }
      }
    });
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const FilterByNameInput = (
      <div>
          <label>Nume</label>
          <br />
          <Search
            allowClear
            placeholder="Căutare după nume"
            onSearch={(value) => {
              setCurrentPage(1);
              setSearchTerm(value || undefined);
            }}
          />
      </div>
    );

    const FilterByLanguageInput = (
      <div>
          <label>Tip</label>
          <br />
          <Select
            allowClear
            placeholder="Filtrare tip"
            style={{ width: "200px" }}
            value={typeFilter}
            onChange={(value) => {
              setCurrentPage(1);
              setTypeFilter(value);
            }}
          >
            {typeOptions.map((type) => (
              <Option key={type} value={type}>
                {competenceTypeLabel(type)}
              </Option>
            ))}
        </Select>
      </div>
    );
  return (
    <Layout>
      <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "16px 0" }}>
            <Pagination 
              total={data?.totalCount}
              current={currentPage ?? 1}
              pageSize={data?.pageSize ?? 10}
              pageSizeOptions={["10", "20", "50"]}
              defaultPageSize={10}
              showSizeChanger={true}
              onChange={handleTableChange}
              showTotal={(total: number, range: [number, number]) => {
                  return <>
                    <Tooltip title="Adaugă competență">
                      <Button style={{marginRight: 10}} type="primary" icon={<PlusOutlined />} onClick={openCreateEditor}>
                        Adaugă competență
                      </Button>
                    </Tooltip>
                    <Button type="default">
                        Total competențe: {data?.totalCount}
                    </Button>
                  </>
              }}
              hideOnSinglePage={ false}
              locale={{ items_per_page: t('configuration.pagination')}}
            >
            </Pagination>
        </div>
        <Table
          className={styles.usersTable}
          dataSource={data?.data || []}
          rowKey={(record) => record.id!}
          loading={isLoading}
          pagination={false}
          columns={[
            {
              title: FilterByNameInput,
              dataIndex: 'description',
              render: (text: string, record: any, index: number) => {
                  return text;
              },
              width: 1200,
            },
            {
              title: FilterByLanguageInput,
              dataIndex: 'type',
              key: 'type',
              width: 240,
              render: (_: any, record: CompetenceTemplateDTO) => competenceTypeLabel(record.type),
            },
            {
              title: "Nr. rezultate asociate",
              key: "results",
              width: 120,
              render: (_: any, record: CompetenceTemplateDTO) => record.associatedResultTemplates?.length || 0,
            },
            {
              title: "Acțiuni",
              key: "actions",
              width: 100,
              render: (_: any, record: CompetenceTemplateDTO) => (
                <div style={{ display: "flex", gap: "8px" }}>
                  <Tooltip title='Editare competență'>
                      <FontAwesomeIcon onClick={() => openEditEditor(record)} icon={solid('edit')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px", marginRight: 10 }}/>
                  </Tooltip>
                  <Tooltip title='Ștergere competență'>
                      <FontAwesomeIcon onClick={() => confirmDelete(record.id)} icon={solid('trash')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                  </Tooltip>
                </div>
              )
            }
          ]}
        />

        <Modal
          open={isEditorOpen}
          title={editingTemplate?.id ? "Editare competență" : "Adăugare competență"}
          onCancel={closeEditor}
          onOk={saveTemplate}
          okText="Salvează"
          cancelText="Renunță"
          width={980}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="description"
              label="Denumire competență"
              rules={[{ required: true, message: "Denumirea competenței este obligatorie!" }]}
            >
              <Input.TextArea rows={2} placeholder="Introduceți denumirea competenței" />
            </Form.Item>
            <Form.Item
              name="type"
              label="Tip competență"
              rules={[{ required: true, message: "Tipul competenței este obligatoriu!" }]}
            >
              <Select placeholder="Alege tipul competenței">
                {typeOptions.map((type) => (
                  <Option key={type} value={type}>
                    {competenceTypeLabel(type)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div style={{ textAlign: "left", marginTop: "8px", marginBottom: "8px" }}>
              Rezultate asociate
            </div>

            <div style={{ textAlign: "left", marginBottom: "8px" }}>a. Cunoștințe</div>
            <ProgramCompetenceForm
              name="knowledgeResults"
              initialValue={[]}
              rules={true}
              allowDeleteLastItem={true}
              identifierPrefix="C"
              buttonText="Adaugă rezultat - Cunoștințe"
            />
            <br />
            <div style={{ textAlign: "left", marginBottom: "8px" }}>b. Abilități</div>
            <ProgramCompetenceForm
              name="skillResults"
              initialValue={[]}
              rules={true}
              allowDeleteLastItem={true}
              identifierPrefix="A"
              buttonText="Adaugă rezultat - Abilități"
            />
            <br />
            <div style={{ textAlign: "left", marginBottom: "8px" }}>c. Responsabilitate și Autonomie</div>
            <ProgramCompetenceForm
              name="responsibilityResults"
              initialValue={[]}
              rules={true}
              allowDeleteLastItem={true}
              identifierPrefix="RA"
              buttonText="Adaugă rezultat - Responsabilitate și Autonomie"
            />
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default CompetenceTemplates;
